import { useEffect, useMemo, useState } from "react";

export const HISTORY_KINDS = [
  "Sequence",
  "Triggers",
  "Copy",
  "Senders",
  "Approval",
  // A real message went to a real inbox. Worth its own filter: it is the one entry that
  // means something left the building (MPN-2206).
  "Test",
] as const;

export type HistoryKind = (typeof HISTORY_KINDS)[number];

export type HistoryEntry = {
  kind: HistoryKind;
  what: string;
  who: string;
  when: string;
  /*
      Coalescing key. Two consecutive changes to the same thing collapse into one entry —
      nudging a trigger from 40 to 45 to 42 is one decision, and three rows for it buries
      everything else.
  */
  key?: string;
};

type Props = {
  campaignName: string;
  entries: HistoryEntry[];
  onClose: () => void;
};

export function HistoryPanel({ campaignName, entries, onClose }: Props) {
  const [filter, setFilter] = useState<HistoryKind | "All">("All");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = useMemo(
    () => (filter === "All" ? entries : entries.filter((e) => e.kind === filter)),
    [entries, filter],
  );

  return (
    <>
      <div className="hist__scrim" onClick={onClose} />
      <aside className="hist" role="dialog" aria-label="History">
        <div className="hist__head">
          <span className="cat__accent" />
          <div className="hist__ident">
            <div className="hist__title">History</div>
            <div className="hist__sub">{campaignName} · Email and Text messages</div>
          </div>
          <button
            type="button"
            className="hist__close"
            aria-label="Close history"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="hist__filters">
          {(["All", ...HISTORY_KINDS] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              className={`hist__filter${filter === kind ? " hist__filter--on" : ""}`}
              onClick={() => setFilter(kind)}
            >
              {kind}
            </button>
          ))}
        </div>

        <div className="hist__list">
          {rows.length === 0 ? (
            <div className="hist__empty">No changes of this kind yet.</div>
          ) : (
            rows.map((entry, index) => (
              <div className="hist__row" key={`${entry.when}-${index}`}>
                <div className="hist__row-head">
                  <span className="hist__kind">{entry.kind}</span>
                  <span className="hist__who">
                    {entry.who} · {entry.when}
                  </span>
                </div>
                <div className="hist__what">{entry.what}</div>
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
}

/** Prepends an entry, collapsing it into the previous one when they share a key. */
export function appendHistory(entries: HistoryEntry[], next: HistoryEntry): HistoryEntry[] {
  const head = entries[0];
  const collapse = next.key != null && head?.key === next.key;
  return [next, ...(collapse ? entries.slice(1) : entries)];
}

export default HistoryPanel;
